import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAsk } from '../contexts/AskContext';
import QuerySection from '../components/Dashboards/QuerySection';
import DashboardManager from '../components/Dashboards/DashboardManager';
import { useConfig } from '../contexts/ConfigContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Bookmark, GitBranch } from 'lucide-react';
import QueryLibrary from '../components/QueryComponent/QueryLibrary';
import useQueryLibrary from '../hooks/useQueryLibrary';
import { createPortal } from 'react-dom';
import RelationshipVisualization from '../components/RelationshipVisualization';



const USER_NAME_CACHE_KEY = 'user_name_cache';

const Ask = ({ mainContentRef }) => {
  const { currentUser, isAdmin, isAIManager, allowedTables } = useAuth();  
  const libraryRef = useRef(null);
  
  // Move all useState and useRef declarations to the top level
  const [userName, setUserName] = useState(() => {
    try {
      const cached = localStorage.getItem(USER_NAME_CACHE_KEY);
      if (cached) {
        const { name, email } = JSON.parse(cached);
        if (email === currentUser?.email) {
          return name;
        }
      }
    } catch (error) {
    }
    return '';
  });
  const [isNameLoading, setIsNameLoading] = useState(!userName);
  const [isLibraryVisible, setIsLibraryVisible] = useState(false);
  const [mobileLibraryOpened, setMobileLibraryOpened] = useState(false);  
  const [tableNamesForTitle, setTableNamesForTitle] = useState([]);
  const [showMoreTables, setShowMoreTables] = useState(false);
  const moreTablesRef = useRef(null);
  const [isUrlQueryLoading, setIsUrlQueryLoading] = useState(false);
  const [hasClickedDataButton, setHasClickedDataButton] = useState(true);
  const [urlQueryError, setUrlQueryError] = useState(null);
  const hasProcessedUrlQuery = useRef(false);
  const [showRelationshipVis, setShowRelationshipVis] = useState(false);
  const [allTables, setAllTables] = useState([]);
  const [isDataOwner, setIsDataOwner] = useState(false);

  const {
    queryResult,
    isLoading,
    setIsLoading,
    error,
    setError,
    isQueryJustAdded,
    setIsQueryJustAdded,
    handleQueryResult,
    handleQueryStart,
    handleClearResult
  } = useAsk();

  const { config, isLoading: isConfigLoading } = useConfig();
  const { 
    addToLibrary, 
    runSavedQuery, 
    queryJustAdded, 
    setQueryJustAdded: setLibraryQueryJustAdded,
    hideQuery,
    saveQuerySettings,
    getQuerySettings,
    filteredQueries: getFilteredQueries
  } = useQueryLibrary();


  // Effect to fetch user's name
  useEffect(() => {
    const fetchUserName = async () => {
      if (!currentUser?.email) return;
      
      try {
        setIsNameLoading(true);
        const userPrefsRef = doc(db, 'registeredUsersPreferences', currentUser.email);
        const docSnap = await getDoc(userPrefsRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const firstName = data.englishName?.split(' ')[0] || '';
          
          // Only show notification if they haven't clicked before
          if (!data.firstDataButtonClick) {
            setHasClickedDataButton(false);
          }
          
          try {
            localStorage.setItem(USER_NAME_CACHE_KEY, JSON.stringify({
              name: firstName,
              email: currentUser.email
            }));
          } catch (error) {
          }

          setUserName(firstName);
        } else {
          // If no user preferences exist, show the notification
          setHasClickedDataButton(false);
        }
      } catch (error) {
      } finally {
        setIsNameLoading(false);
      }
    };

    // Always fetch from DB to ensure we have the latest firstDataButtonClick status
    fetchUserName();
  }, [currentUser]);

  // Check if user has Data_Owner role
  useEffect(() => {
    const checkDataOwnerRole = async () => {
      if (!currentUser?.email) return;
      
      try {
        const userPrefsRef = doc(db, 'registeredUsersPreferences', currentUser.email);
        const docSnap = await getDoc(userPrefsRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const roles = data.roles || [];
          setIsDataOwner(roles.includes('Data_Owner'));
        }
      } catch (error) {
        console.error('Error checking Data_Owner role:', error);
      }
    };
    
    checkDataOwnerRole();
  }, [currentUser]);

  // New effect to check for pending query from URL or localStorage
  useEffect(() => {
    const checkForPendingQuery = async () => {
      // Skip if we've already processed a query or if user is not logged in
      if (hasProcessedUrlQuery.current || !currentUser?.email) return;
    
      try {
        // Check URL parameters first
        const urlParams = new URLSearchParams(window.location.search);
        const urlQueryId = urlParams.get('queryId');
        
        // Check localStorage for pending query IDs (fallback)
        const pendingQueryId = localStorage.getItem('pendingQueryId');
        const pendingLlmQueryId = localStorage.getItem('pendingLlmQueryId');
        
        // Prioritize URL parameter over localStorage
        const queryIdToUse = urlQueryId || pendingLlmQueryId;
        
        if (!urlQueryId && !pendingQueryId && !pendingLlmQueryId) return;
    
        // Mark as processed to prevent duplicate processing
        hasProcessedUrlQuery.current = true;
        setIsUrlQueryLoading(true);
        setUrlQueryError(null);
        
        // Start the query process
        handleQueryStart();
        
        let queryIdToProcess = queryIdToUse;
        let isSavedQueryFormat = false;
        
        // Handle backward compatibility: convert SavedQuery to LLM Query if needed
        if (pendingQueryId && !pendingLlmQueryId && !urlQueryId) {
          isSavedQueryFormat = true;
          
          // Check if the SavedQuery exists
          const queryDocRef = doc(db, 'SavedQueries', pendingQueryId);
          const queryDoc = await getDoc(queryDocRef);
          
          if (!queryDoc.exists()) {
            setUrlQueryError('The specified query does not exist.');
            handleQueryResult(null);
            localStorage.removeItem('pendingQueryId');
            localStorage.removeItem('pendingQueryTimestamp');
            return;
          }
          
          // Get the SavedQuery data
          const queryData = queryDoc.data();
          
          // Check permissions for SavedQuery format
          if (!isAdmin && !isAIManager &&
              queryData.userId !== currentUser.email && 
              queryData.isPrivate && 
              !queryData.sharedByLink) {
            setUrlQueryError('You do not have permission to view this query.');
            handleQueryResult(null);
            localStorage.removeItem('pendingQueryId');
            localStorage.removeItem('pendingQueryTimestamp');
            return;
          }
          
          // Extract the documentId from the SavedQuery to use as LLM query ID
          if (queryData.documentId) {
            queryIdToProcess = queryData.documentId;
          } else {
            setUrlQueryError('Cannot process this query format. Please ask the owner to reshare it.');
            handleQueryResult(null);
            localStorage.removeItem('pendingQueryId');
            localStorage.removeItem('pendingQueryTimestamp');
            return;
          }
        }
                
        // Check if the LLM Query exists
        const queryDocRef = doc(db, 'LLM Queries', queryIdToProcess);
        const queryDoc = await getDoc(queryDocRef);
        
        if (!queryDoc.exists()) {
          setUrlQueryError('The specified query does not exist.');
          handleQueryResult(null);
          
          // Clear URL parameter on error
          if (urlQueryId) {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          }
          
          localStorage.removeItem('pendingQueryId');
          localStorage.removeItem('pendingLlmQueryId');
          localStorage.removeItem('pendingQueryTimestamp');
          return;
        }
        
        // Get the LLM Query data
        const queryData = queryDoc.data();
        
        // Check permissions for LLM Query
        // Allow: 1) Admin or AI Manager (from QueryLog), 2) Query owner, 3) Shared queries
        if (!isAdmin && !isAIManager && 
            queryData.userId !== currentUser.email && 
            !queryData.sharedByLink) {
          setUrlQueryError('You do not have permission to view this query.');
          handleQueryResult(null);
          
          // Clear URL parameter on error
          if (urlQueryId) {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          }
          
          localStorage.removeItem('pendingQueryId');
          localStorage.removeItem('pendingLlmQueryId');
          localStorage.removeItem('pendingQueryTimestamp');
          return;
        }
        
        // Run the query with llmQueryId parameter
        try {
          const response = await fetch(
            `https://us-central1-${config.core.projectId}.cloudfunctions.net/runSavedQuery`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                data: { 
                  llmQueryId: queryIdToProcess, 
                  userId: currentUser.email 
                }
              })
            }
          );
          
          if (!response.ok) {
            throw new Error('Failed to run query');
          }
          
          const data = await response.json();
          
          // Get visualization settings from the Firestore document if available
          const visualizationSettings = queryData.cardSettings || {};
          
          // Process the result
          const processedResult = {
            ...data,
            userQuery: data.userQuery || queryData.userQuestion || 'Shared Query',
            name: data.name || 'Shared Query',
            // If it came from a SavedQuery format originally, mark as saved
            isSavedQuery: isSavedQueryFormat,
            // For SavedQuery format, include the original saved query ID for reference
            ...(isSavedQueryFormat ? { savedQueryId: pendingQueryId } : {}),
            config: {
              usedModel: data.usedModel || queryData.llmModel || 'Unknown',
              fallbackUsed: Boolean(data.fallbackUsed || queryData.fallbackUsed),
            },
            documentId: queryIdToProcess,
            cardSettings: visualizationSettings // Apply the settings from Firestore
          };
          
          handleQueryResult(processedResult);
        } catch (error) {
          setError({
            message: 'Failed to load the shared query',
            status: 'error'
          });
          setUrlQueryError(error.message || 'Failed to load the shared query. Please try again later.');
          handleQueryResult(null);
        }
        
        // Scroll to top after loading
        if (mainContentRef && mainContentRef.current) {
          mainContentRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
        
        // Clear the URL parameter after successful loading to prevent re-runs on refresh
        if (urlQueryId) {
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        }
        
        // Clear the pending query from localStorage after successful loading
        localStorage.removeItem('pendingQueryId');
        localStorage.removeItem('pendingLlmQueryId');
        localStorage.removeItem('pendingQueryTimestamp');
      } catch (error) {
        setUrlQueryError('Failed to load the shared query. Please try again later.');
        handleQueryResult(null);
        
        // Clear URL parameter on error as well
        const urlParams = new URLSearchParams(window.location.search);
        const urlQueryId = urlParams.get('queryId');
        if (urlQueryId) {
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        }
        
        // Clear the pending query from localStorage on error
        localStorage.removeItem('pendingQueryId');
        localStorage.removeItem('pendingLlmQueryId');
        localStorage.removeItem('pendingQueryTimestamp');
      } finally {
        setIsUrlQueryLoading(false);
      }
    };
    
    // Check for pending query when component mounts and dependencies are available
    if (currentUser && config && !isConfigLoading) {
      checkForPendingQuery();
    }
  }, [
    currentUser, 
    config, 
    isConfigLoading, 
    runSavedQuery, 
    handleQueryResult, 
    handleQueryStart, 
    setIsLoading, 
    setError,
    mainContentRef,
    isAdmin,
    isAIManager  // Add isAIManager to dependencies
  ]);

  // Sync between the two "just added" states (from useAsk and useQueryLibrary)
  useEffect(() => {
    if (queryJustAdded) {
      setIsQueryJustAdded(true);
    }
  }, [queryJustAdded, setIsQueryJustAdded]);

  useEffect(() => {
    if (isQueryJustAdded) {
      setLibraryQueryJustAdded(true);
    }
  }, [isQueryJustAdded, setLibraryQueryJustAdded]);

// Effect to fetch proper table names for title
useEffect(() => {
  const fetchTableNamesForTitle = async () => {
    try {
      if (!currentUser?.email) return;
      
      // Fetch the table configuration
      const configDoc = await getDoc(doc(db, 'appConfiguration', 'tables'));
      if (!configDoc.exists()) return;
      
      const docData = configDoc.data();
      if (!docData || typeof docData !== 'object') return;
      
      // Create a map of table IDs to table names for lookup
      const tableIdToNameMap = {};
      Object.entries(docData).forEach(([name, value]) => {
        if (value && typeof value === 'object' && value.id) {
          tableIdToNameMap[value.id] = name;
        }
      });
      
      // Filter tables by user's access permissions
      const tableEntries = Object.entries(docData)
        .filter(([_, value]) => 
          value && 
          typeof value === 'object' && 
          value.schema && 
          Array.isArray(value.schema) &&
          value.id && // Ensure table has an ID
          allowedTables && // Check if allowedTables exists
          allowedTables[value.id] // Check if table is allowed
        );
      
      // For relationship visualization, prepare all accessible tables
      const tablesForVis = tableEntries.map(([name, value]) => ({
        id: value.id,
        name: name,
        primaryKey: value.primaryKey || null,
        foreignKeys: value.foreignKeys || [],
        isMaterializedView: value.isMaterializedView || false,
        motherTable: value.motherTable
      }));
      setAllTables(tablesForVis);
      
      // Process table names, handling motherTable case
      const tableNames = tableEntries.map(([name, value]) => {
        // If this is a materialized view with a motherTable
        if (value.isMaterializedView && value.motherTable) {
          // Extract the mother table ID (e.g., "tango-dev-b72e4.view.descrip")
          const motherTableId = value.motherTable;
          
          // Look up the mother table name using the ID-to-name map
          const motherTableName = tableIdToNameMap[motherTableId];
          
          // Use the mother table name if found, otherwise fallback to this table's name
          return formatTableName(motherTableName || name);
        } else {
          // Use this table's name directly
          return formatTableName(name);
        }
      }).filter(name => name);
      
      // Remove duplicates that might arise from multiple views of the same mother table
      const uniqueTableNames = [...new Set(tableNames)];
      
      setTableNamesForTitle(uniqueTableNames);
    } catch (err) {
    }
  };
  
  fetchTableNamesForTitle();
}, [currentUser, allowedTables]);
  

  // Handle library close function
  const handleLibraryClose = () => {
    setIsLibraryVisible(false);
    setMobileLibraryOpened(false);
  };
  
  const submitQuery = useCallback(
    async (query, isFollowUp = false, highlightedText = '', originalSqlQuery = '') => {
      handleQueryStart();
      try {
        const userId = currentUser?.email || 'anonymous';
        const userQuery = query;

        const response = await fetch(
          'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/processDynamicQueryWithCORS',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              data: {
                userQuery: userQuery,
                userId: userId,
                isFollowUp: isFollowUp,
                isAdmin: isAdmin,
                highlightedText: highlightedText,
                originalSqlQuery: originalSqlQuery,
                originalDocumentId: queryResult ? queryResult.documentId : null,
                originalSavedQueryId: queryResult ? queryResult.savedQueryId : null,
              },
              context: { auth: true },
            }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error('Failed to process query.');
        }

        const newResult = {
          result: data.result || [],
          sqlQuery: data.sqlQuery || '',
          schema: data.schema || '',
          userQuery: data.userQuery || query,
          truncationMessage: data.truncationMessage || '',
          truncated: data.truncated || false,
          explanation: data.explanation || '',
          isFollowUp: data.isFollowUp || isFollowUp,
          originalQuery: data.originalQuery || '',
          documentId: data.documentId || (queryResult ? queryResult.documentId : null),
          savedQueryId: data.savedQueryId || (queryResult ? queryResult.savedQueryId : null),
          config: {
            usedModel: data.usedModel || 'Unknown',
            fallbackUsed: data.fallbackUsed || false,
          },
          retryCount: data.retryCount || 0,
          retryResult: data.retryResult || 'N/A',
          bigQueryError: data.bigQueryError || null,
          status: data.status || 'completed',
          endTime: data.endTime || new Date().toISOString(),
        };

        handleQueryResult(newResult);
      } catch (error) {
        setError({
          message: 'An unexpected error occurred. Please try again.',
          status: 'error',
        });
        handleQueryResult(null);
      }
    },
    [currentUser, handleQueryStart, handleQueryResult, queryResult, isAdmin, setError, config]
  );

  const handleFollowUpQuery = useCallback(
    (followUpQuery, highlightedText, originalSqlQuery) => {
      submitQuery(followUpQuery, true, highlightedText, originalSqlQuery);
    },
    [submitQuery]
  );

  const handleSaveQuery = useCallback(async (name, isPrivate = true) => {
    if (!queryResult) return;

    try {
      const response = await fetch(
        'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/saveQuery',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              name,
              userQuery: queryResult.userQuery,
              sqlQuery: queryResult.sqlQuery,
              userId: currentUser.email,
              isPrivate,
              documentId: queryResult.documentId,
              explanation: queryResult.explanation,
              hidden: false // Explicitly set hidden to false for new queries
            },
            context: { auth: true },
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save query');
      }

      setIsQueryJustAdded(true);
      setTimeout(() => setIsQueryJustAdded(false), 3000);
    } catch (error) {
      setError({
        message: 'Failed to save query. Please try again.',
        status: 'error',
      });
    }
  }, [queryResult, currentUser, setError, setIsQueryJustAdded, config]);

  const handleAddToDashboard = useCallback(async (dashboardId, name) => {
    if (!queryResult) return;
    
    try {
      const savedQueryResponse = await fetch(
        'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/saveQuery',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              name,
              userQuery: queryResult.userQuery,
              sqlQuery: queryResult.sqlQuery,
              userId: currentUser.email,
              isPrivate: true,
              documentId: queryResult.documentId,
              explanation: queryResult.explanation,
              hidden: false // Explicitly set hidden to false for new queries
            },
            context: { auth: true },
          }),
        }
      );

      if (!savedQueryResponse.ok) {
        throw new Error('Failed to save query');
      }

      const savedQueryData = await savedQueryResponse.json();
      
      const dashboardResponse = await fetch(
        'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/handleDashboardOperation',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              operation: 'addQueryToDashboard',  // <-- FIXED
              dashboardId,
              query: { id: savedQueryData.queryId },  // <-- Also need to wrap in query object
              userId: currentUser.email,
            },
            context: { auth: true },
          }),
        }
      );

      if (!dashboardResponse.ok) {
        throw new Error('Failed to add query to dashboard');
      }

      setIsQueryJustAdded(true);
      setTimeout(() => setIsQueryJustAdded(false), 3000);
    } catch (error) {
      setError({
        message: 'Failed to add query to dashboard. Please try again.',
        status: 'error',
      });
    }
  }, [queryResult, currentUser, setError, setIsQueryJustAdded, config]);

  // Use the hook's addToLibrary function
  const handleAddToLibrary = useCallback(async (result) => {
    if (!result) return;
    
    try {
      const response = await addToLibrary(result);
      // Explicitly refresh the library
      if (libraryRef.current) {
        libraryRef.current.refresh();
      }
      return response;
    } catch (error) {
      setError({
        message: 'Failed to add to library. Please try again.',
        status: 'error',
      });
      throw error;
    }
  }, [addToLibrary, setError]);
  
  // Use the hook's runSavedQuery function  
  const handleLibraryQuerySelect = useCallback((query) => {
    // Scroll to top before showing loading state
    if (mainContentRef && mainContentRef.current) {
      mainContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    
    
    runSavedQuery(query, handleQueryResult, handleQueryStart, setIsLoading, setError)
      .then(() => {
        // Close the library after selecting a query on mobile
        if (window.innerWidth < 768) {
          setIsLibraryVisible(false);
        }
      })
      .catch(error => {
      });
  }, [runSavedQuery, handleQueryResult, handleQueryStart, setIsLoading, setError, mainContentRef]);

  const handleDashboardUpdate = useCallback(() => {
    // Implement if needed for future dashboard-related functionality
  }, []);

  const dashboardManager = DashboardManager({
    setError,
    handleQueryResult,
    currentUser,
    isAdmin,
  });

  if (!currentUser) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold mb-4">Please log in to view this page</h2>
        <p>You need to be logged in to access the query features.</p>
      </div>
    );
  }

  // Container classes - base container with XL breakpoint for wider screens
  const containerClasses = `
    container mx-auto px-4 sm:px-6 flex flex-col items-center justify-center 
    min-h-[calc(100vh-64px)] max-w-4xl xl:max-w-[1400px]
  `;

  // Query container - slightly wider initial state, full width when showing results
  const queryContainerClasses = `
  relative mx-auto 
  transition-all duration-500 ease-in-out
  ${queryResult 
    ? 'w-full xl:w-full' // Full width when showing results
    : 'w-full sm:w-[90%] md:w-[650px] lg:w-[700px] xl:w-[700px]' // Slightly wider than before
  }
  `;

  // Enhanced title classes with smooth transition for name
  const titleClasses = `
    text-2xl md:text-3xl font-bold mb-8 text-center text-gray-700
    transition-all duration-500 ease-in-out transform
    ${queryResult 
      ? 'scale-90 opacity-0 absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none' 
      : 'scale-100 opacity-100 relative'
    }
  `;

  // Name specific transition classes
  const nameClasses = `
    inline-block
    transition-all duration-300 ease-in-out
    ${isNameLoading ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'}
  `;

  // Format table name for display
  const formatTableName = (name) => {
    if (!name) return '';
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };


// Handle first click on data button
const handleDataButtonClick = async () => {
  setShowMoreTables(true);
  
  // If this is the first click, save timestamp
  if (!hasClickedDataButton && currentUser?.email) {
    try {
      const userPrefsRef = doc(db, 'registeredUsersPreferences', currentUser.email);
      await setDoc(userPrefsRef, {
        firstDataButtonClick: new Date()
      }, { merge: true });
      setHasClickedDataButton(true);
    } catch (error) {
      console.error('Error saving first click timestamp:', error);
    }
  }
};
  
// Helper function to render the title text based on table count
const renderTitleText = () => {
  if (!userName) {
    return <span className="text-black">Ask about your data</span>;
  }

  const tableCount = tableNamesForTitle.length;
  
  return (
    <>
      <span className={nameClasses + " text-black"}>{userName}</span>
      <span className="text-black">, ask about your </span>
      {tableCount > 0 ? (
        <div 
          className="relative inline-block" 
          ref={moreTablesRef}
        >
          <button 
            className={
              hasClickedDataButton 
                ? "relative text-gray-700 bg-transparent inline-flex items-center gap-2 group transition-all hover:text-primary"
                : "relative bg-white px-4 py-1 rounded-full transition-all inline-flex items-center gap-2 group shadow-[0_4px_15px_rgba(124,58,237,0.2)] hover:shadow-[0_8px_25px_rgba(124,58,237,0.3)] hover:-translate-y-1"
            }
            onClick={handleDataButtonClick}
          >
            <span className={`relative ${hasClickedDataButton ? "" : "text-primary font-bold"}`}>
              data
              {hasClickedDataButton && (
                <span 
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gray-300 to-transparent transition-all duration-300 group-hover:via-primary"
                />
              )}
            </span>
            {!hasClickedDataButton && tableCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-secondary to-primary text-white text-xs min-w-[22px] h-[22px] px-1 rounded-full font-semibold flex items-center justify-center animate-bounce">
                {tableCount}
              </span>
            )}
            <svg 
              className={`w-3.5 h-3.5 transition-all duration-200 group-hover:translate-y-0.5 ${
                hasClickedDataButton 
                  ? "text-gray-400 group-hover:text-primary" 
                  : "text-primary opacity-60"
              }`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 9l-7 7-7-7" 
              />
            </svg>
          </button>
          
          {showMoreTables && createPortal(
            <div 
              className="fixed inset-0 z-[9998] flex items-center justify-center" 
              onClick={(e) => {
                // Only close if clicking the backdrop itself (not the modal)
                if (e.target === e.currentTarget) {
                  setShowMoreTables(false);
                }
              }}
            >
              <div 
                className="tables-dropdown bg-white rounded-lg shadow-xl border border-gray-200 py-3 w-[320px]"
                style={{
                  maxHeight: '80vh'
                }}
                onClick={(e) => {
                  // Prevent any clicks inside the modal from bubbling up
                  e.stopPropagation();
                }}
              >
                <div className="px-4 pb-3 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm text-gray-500 font-medium uppercase tracking-wider">Available Data</div>
                    <button
                      onClick={() => setShowMoreTables(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Tango can answer questions and perform analysis exclusively on the data sources listed below.
                  </p>
                </div>
                
                <div className="overflow-y-auto max-h-[calc(80vh-140px)] py-2">
                  {tableNamesForTitle.map((tableName, index) => (
                    <div key={index} className="px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      {tableName}
                    </div>
                  ))}
                </div>
                
                <div className="px-4 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400 text-center">
                    To add other data sources, contact your system admin
                  </p>
                  
                  {/* Relationship Visualization Button - Only for admins or Data Owners */}
                  {(isAdmin || isDataOwner) && (
                    <button
                      onClick={() => {
                        setShowMoreTables(false);
                        setShowRelationshipVis(true);
                      }}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                    >
                      <GitBranch className="h-4 w-4" />
                      View Table Relationships
                    </button>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      ) : (
        <span className="text-gray-500">data</span>
      )}
    </>
  );
};

// Display URL query error if any
const renderUrlQueryError = () => {
  if (!urlQueryError) return null;
  
  return (
    <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 rounded shadow-sm w-full">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-700">
            {urlQueryError}
          </p>
        </div>
      </div>
    </div>
  );
};

return (
  <div className="flex h-full">
    {/* Library toggle button (visible on mobile) */}
    <button
      onClick={() => {
        const willBeVisible = !isLibraryVisible;
        setIsLibraryVisible(willBeVisible);
        if (willBeVisible) {
          setMobileLibraryOpened(true);
        }
      }}
      className="fixed bottom-5 left-5 z-30 md:hidden bg-white p-2 rounded-full shadow-lg border border-gray-200 hover:shadow-xl transition-shadow"
      aria-label="Toggle query library"
    >
      <Bookmark size={20} className="text-primary" />
    </button>

    {/* Mobile overlay when library is visible */}
    {isLibraryVisible && (
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-10 md:hidden"
        onClick={() => {
          setIsLibraryVisible(false);
          setMobileLibraryOpened(false);
        }}
      ></div>
    )}

    {/* Library panel - floating cards without container */}
    <div className={`fixed md:static md:block top-0 left-0 h-screen md:h-[calc(100vh-64px)] z-20 md:z-auto overflow-hidden
                md:border-r border-gray-100
                transform transition-transform duration-300 ease-in-out
                ${isLibraryVisible ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
      <QueryLibrary 
        ref={libraryRef}
        onQuerySelect={handleLibraryQuerySelect}
        isVisible={true}
        onClose={handleLibraryClose}
        hasQueryResult={!!queryResult}
        mobileLibraryOpened={mobileLibraryOpened}
        onMobileLibraryOpenedChange={setMobileLibraryOpened}
        getQuerySettings={getQuerySettings}  // Pass the same instance
      />
    </div>
      <div className="flex-1 flex flex-col items-center justify-start overflow-y-auto">
        <div className={containerClasses}>
          <div className={titleClasses}>
            {renderTitleText()}
          </div>

          {/* Show URL query error if any */}
          {renderUrlQueryError()}

          {/* Show loading indicator specifically for URL query loading */}
          {isUrlQueryLoading && (
            <div className="w-full flex justify-center py-4 mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          )}

          <div className={queryContainerClasses}>
          <QuerySection
            onQueryResult={handleQueryResult}
            onQueryStart={handleQueryStart}
            queryResult={queryResult}
            isLoading={isLoading || isConfigLoading || isUrlQueryLoading}
            error={error}
            currentUser={currentUser}
            onSaveQuery={handleSaveQuery}
            onAddToDashboard={handleAddToDashboard}
            onFollowUpQuery={handleFollowUpQuery}
            mainContentRef={mainContentRef}
            submitQuery={submitQuery}
            onDashboardUpdate={handleDashboardUpdate}
            dashboardManager={dashboardManager}
            isQueryJustAdded={isQueryJustAdded}
            setIsQueryJustAdded={setIsQueryJustAdded}
            isAskPage={true}
            onClearResult={handleClearResult}
            onAddToLibrary={handleAddToLibrary}
            saveQuerySettings={saveQuerySettings}
            getQuerySettings={getQuerySettings}
            getFilteredQueries={getFilteredQueries}
          />
          </div>
        </div>
        </div>
      
      {/* Relationship Visualization Modal */}
      {showRelationshipVis && (
        <RelationshipVisualization
          tables={allTables}
          onClose={() => setShowRelationshipVis(false)}
        />
      )}
    </div>
  );
};

export default Ask;